import { useEffect, useMemo, useRef } from 'react';
import {
  type CustomerFeatureFlags,
  readDevFeatureFlagOverrides,
  resolveCustomerFeatureFlags,
  resolveEffectiveGiftSubFlags,
} from '../../config/customerFeatureFlags.js';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerFeatureFlagsQuery } from './customerFeatureFlagsApi.js';
import { useQueryClient } from '@tanstack/react-query';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { customerFeatureFlagsBus, publishCustomerFeatureFlagsRevision } from '../../shared/crossTab/customerCrossTab.js';
import { useCustomerSurfaceEntitlement } from '../entitlements/customerEntitlementGuards.js';

export interface UseCustomerFeatureFlagsResult {
  readonly flags: CustomerFeatureFlags;
  readonly effectiveGiftSubFlags: Pick<
    CustomerFeatureFlags,
    | 'customerGiftDonateMenuEnabled'
    | 'customerGiftRecurringDonationsEnabled'
    | 'customerGiftMyDonationsEnabled'
  >;
  readonly flagsReady: boolean;
  readonly pollRevision: number | null;
  /** Self-Scan selective-check capability (FR-16 / FR-17). From poll; default OFF. */
  readonly tenantSelfScanSelectiveCheckEnabled: boolean;
  /** Sample rate % when selective check is ON. */
  readonly samplePercent: number;
}

function mergeBootAndPoll(
  boot: CustomerFeatureFlags,
  poll: {
    readonly giftSurfacesMaster: boolean;
    readonly giftDonate: boolean;
    readonly giftRecurring: boolean;
    readonly giftMyDonations: boolean;
  } | null,
): CustomerFeatureFlags {
  if (poll === null) {
    return boot;
  }
  return resolveCustomerFeatureFlags({
    ...boot,
    customerGiftSurfacesMasterEnabled:
      boot.customerGiftSurfacesMasterEnabled && poll.giftSurfacesMaster,
    customerGiftDonateMenuEnabled: boot.customerGiftDonateMenuEnabled && poll.giftDonate,
    customerGiftRecurringDonationsEnabled:
      boot.customerGiftRecurringDonationsEnabled && poll.giftRecurring,
    customerGiftMyDonationsEnabled: boot.customerGiftMyDonationsEnabled && poll.giftMyDonations,
  });
}

export function useCustomerFeatureFlags(): UseCustomerFeatureFlagsResult {
  const runtimeConfig = useRuntimeConfig();
  const pollQuery = useCustomerFeatureFlagsQuery();
  const queryClient = useQueryClient();
  const tenantCode = useTenantCode();
  const { isLoading: entitlementsLoading } = useCustomerSurfaceEntitlement();
  const lastRevisionRef = useRef<number | null>(null);

  const bootFlags = useMemo(
    () =>
      resolveCustomerFeatureFlags({
        ...runtimeConfig.featureFlags,
        ...readDevFeatureFlagOverrides(),
      }),
    [runtimeConfig.featureFlags],
  );

  const flags = useMemo(
    () => mergeBootAndPoll(bootFlags, pollQuery.data ?? null),
    [bootFlags, pollQuery.data],
  );

  const effectiveGiftSubFlags = useMemo(
    () => resolveEffectiveGiftSubFlags(flags),
    [flags],
  );

  const flagsReady =
    !entitlementsLoading && (!pollQuery.isLoading || pollQuery.isError);

  useEffect(() => {
    const revision = pollQuery.data?.revision;
    if (revision === undefined || revision === lastRevisionRef.current) {
      return;
    }
    lastRevisionRef.current = revision;
    publishCustomerFeatureFlagsRevision(revision);
  }, [pollQuery.data?.revision]);

  useEffect(() => {
    return customerFeatureFlagsBus.subscribe((message) => {
      if (message.type !== 'flags-revision-changed') {
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: customerQueryKeys.featureFlags(tenantCode),
      });
    });
  }, [queryClient, tenantCode]);

  return {
    flags,
    effectiveGiftSubFlags,
    flagsReady,
    pollRevision: pollQuery.data?.revision ?? null,
    tenantSelfScanSelectiveCheckEnabled:
      pollQuery.data?.tenantSelfScanSelectiveCheckEnabled === true,
    samplePercent: pollQuery.data?.samplePercent ?? 10,
  };
}
